import './App.css';
import React from 'react';
import Box from '@mui/material/Box';

import {useExtractData} from './hooks/useExtractData';
import {SOURCES_CONFIG, SOURCE_DATA_MAP} from './constants/sources';
import {exportToCsv} from './utils/csvUtils';

import DataToolbar from './components/DataToolbar';
import LoadingPanel from './components/LoadingPanel';
import {ProcessingPanel, TableLoadingPanel} from './components/StatusPanels';
import ResultGrid from './components/ResultGrid';

// ── Composant principal ───────────────────────────────────────────────────────

export default function App() {

    // ── Paramètres de recherche ───────────────────────────────────────────────
    const [localisation, setLocalisation] = React.useState(34274);
    const [typeLoc,      setTypeLoc]      = React.useState('commune');
    const [filter,       setFilter]       = React.useState({
        typesDeBiens: ['SURFACE_UTILE'], syndic: true, copro: true,
        nbLgts: 0, Dpes: ['string'], output: 'BAN_LAT_LON',
    });
    const [csvSeparator, setCsvSeparator] = React.useState(';');

    // ── Données et états via le hook ──────────────────────────────────────────
    const {csvData, colons, loading, processing, tableReady, sourcesWithState} =
        useExtractData(filter);

    // ── Sélection de sources ──────────────────────────────────────────────────
    const [selectedSources, setSelectedSources] = React.useState(new Set());

    /** Sources présentes dans les données chargées (avec au moins une colonne). */
    const availableSources = React.useMemo(() =>
        SOURCES_CONFIG.filter(s => colons[s.key] && Object.keys(colons[s.key]).length > 0),
        [colons]
    );

    // Quand de nouvelles données arrivent, tout sélectionner par défaut.
    React.useEffect(() => {
        const keys = availableSources.map(s => s.key);
        if (keys.length > 0) setSelectedSources(new Set(keys));
    }, [availableSources]);

    // ── Colonnes DataGrid : adresse fixe + une colonne par champ de chaque source ──

    const columns = React.useMemo(() => {
        if (!csvData || !colons) return [];
        return [
            {
                field: 'batiment_groupe_id',
                headerName: 'batiment_groupe_id',
                width: 220,
                headerClassName: 'col-header--address',
                renderCell: p => (
                    <Box component="span" sx={{fontFamily: 'monospace', fontWeight: 600, fontSize: '0.71rem'}}>
                        {p.value}
                    </Box>
                ),
            },
            {
                field: 'latitude',
                headerName: 'Latitude',
                width: 120,
                type: 'number',
                headerClassName: 'col-header--synthese',
            },
            {
                field: 'longitude',
                headerName: 'Longitude',
                width: 120,
                type: 'number',
                headerClassName: 'col-header--synthese',
            },
            {
                field: 'surface_utile',
                headerName: 'Surface utile (m²)',
                width: 150,
                type: 'number',
                headerClassName: 'col-header--synthese',
            },
            ...Object.entries(colons).flatMap(([src, meta]) =>
                Object.keys(meta).map(col => ({
                    field: `${src}.${col}`,
                    headerName: col,
                    description: `${src} → ${col}`,
                    width: 150,
                    type: meta[col]?.type === 'number' ? 'number' : 'string',
                    headerClassName: `col-header--${src}`,
                    renderCell: p => {
                        const v = p.value;
                        if (!v && v !== 0)
                            return <Box component="span" sx={{color: 'text.disabled'}}>—</Box>;
                        const parts = String(v).split(' | ');
                        if (parts.length === 1) return <span>{v}</span>;
                        return (
                            <span>
                                {parts.map((part, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <Box component="span"
                                                        sx={{color: 'primary.light', mx: 0.3, fontWeight: 'bold'}}>|</Box>}
                                        {part}
                                    </React.Fragment>
                                ))}
                            </span>
                        );
                    },
                }))
            ),
        ];
    }, [csvData, colons]);

    /** Groupes de colonnes colorés par source. */
    const columnGroupingModel = React.useMemo(() => [
        {
            groupId: 'synthese',
            headerName: 'Synthèse',
            headerClassName: 'col-group--synthese',
            children: [{field: 'latitude'}, {field: 'longitude'}, {field: 'surface_utile'}],
        },
        ...Object.entries(colons).map(([src, meta]) => ({
            groupId: src,
            headerName: SOURCES_CONFIG.find(s => s.key === src)?.label || src,
            headerClassName: `col-group--${src}`,
            children: Object.keys(meta).map(col => ({field: `${src}.${col}`})),
        })),
    ], [colons]);

    /** Lignes DataGrid : id = batiment_groupe_id, toutes les données aplaties. */
    const rows = React.useMemo(() => {
        if (!csvData || !colons) return [];
        return Object.entries(csvData).map(([bgId, entry]) => {
            const row = {
                id: bgId,
                batiment_groupe_id: bgId,
                latitude:      entry.batimentGroupeAdresses[0]?.latitude  ?? null,
                longitude:     entry.batimentGroupeAdresses[0]?.longitude ?? null,
                surface_utile: entry.surface_utile ?? null,
            };
            Object.entries(colons).forEach(([src, meta]) => {
                const items = SOURCE_DATA_MAP[src]?.(entry) || [];
                Object.keys(meta).forEach(col => {
                    row[`${src}.${col}`] = items.map(it => it[col] ?? '').filter(Boolean).join(' | ');
                });
            });
            return row;
        });
    }, [csvData, colons]);

    // ── Filtrage par sources sélectionnées ────────────────────────────────────

    const FIXED_FIELDS = new Set(['batiment_groupe_id', 'latitude', 'longitude', 'surface_utile']);

    const filteredColumns = React.useMemo(() =>
        columns.filter(col =>
            FIXED_FIELDS.has(col.field) || selectedSources.has(col.field.split('.')[0])
        ),
        [columns, selectedSources]
    );

    const filteredColumnGroupingModel = React.useMemo(() =>
        columnGroupingModel.filter(g => g.groupId === 'synthese' || selectedSources.has(g.groupId)),
        [columnGroupingModel, selectedSources]
    );

    /** Colons restreints aux sources sélectionnées (utilisé pour l'export CSV). */
    const filteredColons = React.useMemo(() =>
        Object.fromEntries(Object.entries(colons).filter(([src]) => selectedSources.has(src))),
        [colons, selectedSources]
    );

    // ── Rendu ─────────────────────────────────────────────────────────────────

    return (
        <Box sx={{width: '100%', minHeight: '100vh', bgcolor: '#f5f6fa'}}>

            <DataToolbar
                localisation={localisation} setLocalisation={setLocalisation}
                typeLoc={typeLoc} setTypeLoc={setTypeLoc}
                loading={loading} csvData={csvData}
                rows={rows} columns={filteredColumns}
                csvSeparator={csvSeparator} setCsvSeparator={setCsvSeparator}
                onLoad={() => setFilter({...filter, geo: {[typeLoc]: localisation}})}
                onExport={() => exportToCsv(csvData, filteredColons, csvSeparator)}
                availableSources={availableSources}
                selectedSources={selectedSources}
                setSelectedSources={setSelectedSources}
            />

            {loading && <LoadingPanel sources={sourcesWithState}/>}

            {processing && <ProcessingPanel/>}

            {csvData && !loading && !processing && !tableReady && (
                <TableLoadingPanel rowsCount={rows.length} columnsCount={columns.length - 1}/>
            )}

            {csvData && !loading && !processing && tableReady && rows.length > 0 && (
                <ResultGrid rows={rows} columns={filteredColumns} columnGroupingModel={filteredColumnGroupingModel}/>
            )}
        </Box>
    );
}
